import {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';

import { useHistory } from 'react-router-dom';
import { LOGIN, LOGOUT, INIT_USER, GET_NOTIFICATIONS } from './types';
import storageUtil from '../../utils/localStorage';
import { userAPI, notificationAPI } from '../../service/api';

const initialUserData = {
  isAuth: false,
  id: '',
  role: '',
  posts: [],
  likes: [],
  username: '',
};

const UserContext = createContext(initialUserData);
export const useUserContext = () => useContext(UserContext);

const TOKEN_STORE_KEY = 'TOKEN';

const reducer = (state, action) => {
  switch (action.type) {
    case LOGIN: {
      const { _id, role, posts, likes, notifications, username, email } =
        action.payload;
      return {
        ...state,
        isAuth: true,
        id: _id,
        role,
        posts,
        likes,
        username,
        email,
        notifications,
      };
    }
    case LOGOUT: {
      return initialUserData;
    }
    case INIT_USER: {
      const { _id, role, posts, likes, username, notifications, email } =
        action.payload;
      return {
        ...state,
        isAuth: true,
        id: _id,
        role,
        posts,
        likes,
        username,
        email,
        notifications,
      };
    }
    case GET_NOTIFICATIONS: {
      const { notifications } = action.payload;

      return {
        ...state,
        notifications,
      };
    }

    default:
      console.error('잘못된 타입입니다');
  }
};

const UserProvider = ({ children }) => {
  const [user, dispatch] = useReducer(reducer, initialUserData);
  const history = useHistory();

  const handleLogIn = useCallback(
    async (data) => {
      const { user, token } = await userAPI.logIn(data);
      const notifications = await notificationAPI.getNotifications();

      storageUtil.setItem(TOKEN_STORE_KEY, token);
      dispatch({ type: LOGIN, payload: { ...user, notifications } });

      // * update state on unmounted component 해결하기 위한 setTimeout
      setTimeout(() => history.replace('/'), 0);
    },
    [history],
  );

  const handleLogOut = useCallback(async () => {
    const payload = await userAPI.logOut();

    storageUtil.removeItem(TOKEN_STORE_KEY);
    dispatch({ type: LOGOUT, payload });
  }, []);

  const handleGetUser = useCallback(async (token) => {
    const payload = await userAPI.getUser(token);
    const notifications = await notificationAPI.getNotifications();

    dispatch({
      type: INIT_USER,
      payload: { ...payload, notifications },
    });
  }, []);

  const handleName = useCallback(async (data) => {
    try {
      const payload = await userAPI.updateName(data);
      dispatch({ type: INIT_USER, payload });

      return true;
    } catch ({ response }) {
      const { status, data } = response;
      if (status === 400) {
        // TODO: 사용자에게 에러 내용 알리기
        console.log(data);
      }

      return false;
    }
  }, []);

  const handleGetNotifications = useCallback(async () => {
    const notifications = await notificationAPI.getNotifications();

    dispatch({
      type: GET_NOTIFICATIONS,
      payload: { notifications },
    });
  }, []);

  const handleReadAllNotifications = useCallback(async () => {
    await notificationAPI.readAllNotifications();
    const notifications = await notificationAPI.getNotifications();

    dispatch({
      type: GET_NOTIFICATIONS,
      payload: { notifications },
    });
  }, []);

  useEffect(() => {
    const token = storageUtil.getItem(TOKEN_STORE_KEY, '');
    if (token) {
      handleGetUser(token);
    }
  }, [handleGetUser]);

  return (
    <UserContext.Provider
      value={{
        user,
        handleLogIn,
        handleLogOut,
        handleGetUser,
        handleName,
        handleGetNotifications,
        handleReadAllNotifications,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default UserProvider;
